/* Ask Amon: say what you want in a sentence.
 *
 * The agent was the thing people were invited to try, and the React client had
 * no way to reach it. The server already did the careful part: POST /agent
 * classifies a sentence and answers with a *proposal* — a title, the lines of
 * what would happen, and either a named action or a rebuilt day — and writes
 * nothing. This window shows that proposal and waits:
 *
 *   · an action is carried out through POST /agent/act, which re-validates it;
 *   · a rebuilt day is applied by asking again with `apply: true`;
 *   · anything it did not understand stays a suggestion list, never a guess.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post, errorMessage } from '../api';
import Dialog from './Dialog';
import Icon from './Icon';

interface Line { op: string; sign: string; t: string; r: string }
interface Reply {
  title?: string;
  lines?: Line[];
  foot?: string;
  speech?: string;
  unsure?: boolean;
  action?: { ok: boolean; action: { kind: string; args: Record<string, unknown> }; outward?: boolean };
  proposal?: { blocks: unknown[] };
  applied?: boolean;
}

const EXAMPLES = ['Plan my day', 'Add a task to write the funding memo, 2h', 'What is left today?'];

export default function AskAmon({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [asked, setAsked] = useState('');
  const [reply, setReply] = useState<Reply | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: async (sentence: string) => {
      const r = await post<Reply>('/api/agent', { text: sentence });
      if (!r.ok) throw new Error(errorMessage(r, 'Amon could not answer that.'));
      return r as Reply;
    },
    onSuccess: (r, sentence) => { setReply(r); setAsked(sentence); setDone(null); }
  });

  const confirm = useMutation({
    mutationFn: async () => {
      if (reply?.action?.ok) {
        const { kind, args } = reply.action.action;
        const r = await post('/api/agent/act', { kind, args });
        if (!r.ok) throw new Error(errorMessage(r, 'That could not be done.'));
      } else if (reply?.proposal) {
        const r = await post<Reply>('/api/agent', { text: asked, apply: true });
        if (!r.ok) throw new Error(errorMessage(r, 'The day could not be saved.'));
        if (!r.applied) throw new Error('The day could not be saved — something on it clashes. Try again.');
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries();
      setDone(reply?.title?.replace(/\?$/, '') ?? 'Done');
      setReply(null);
      setText('');
    }
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (text.trim()) ask.mutate(text.trim());
  };

  const canConfirm = !!reply && !reply.unsure && (!!reply.action?.ok || !!reply.proposal);
  const error = (ask.error as Error | null)?.message ?? (confirm.error as Error | null)?.message ?? null;

  return (
    <Dialog title="Ask Amon" icon="sparkle" onClose={onClose}
            description="Say what you want. Nothing changes until you confirm it.">
      <form className="ask-form" onSubmit={submit}>
        <input value={text} maxLength={500} aria-label="What would you like?"
               placeholder="Plan my afternoon, add a task, what is left today…"
               onChange={e => setText(e.target.value)} />
        <button className="btn btn-primary btn-sm" type="submit" disabled={ask.isPending || !text.trim()}>
          <Icon name="send" size={15} />{ask.isPending ? 'Thinking…' : 'Ask'}
        </button>
      </form>

      {!reply && !done && (
        <div className="inline wrap ask-examples">
          {EXAMPLES.map(x => (
            <button key={x} type="button" className="pill" onClick={() => { setText(x); ask.mutate(x); }}>{x}</button>
          ))}
        </div>
      )}

      {error && <p className="auth-err" role="alert">{error}</p>}

      {done && (
        <p className="notice" role="status"><Icon name="check" size={15} />{done}.</p>
      )}

      {reply && (
        <div className="ask-reply" aria-live="polite">
          {reply.title && <h3>{reply.title}</h3>}
          {!!reply.lines?.length && (
            <ul className="ask-lines">
              {reply.lines.map((l, i) => (
                <li key={i} className={`op-${l.op}`}>
                  <span className="ask-sign" aria-hidden="true">{l.sign}</span>
                  <span><strong>{l.t}</strong>{l.r && <span className="muted small"> — {l.r}</span>}</span>
                </li>
              ))}
            </ul>
          )}
          {reply.foot && <p className="muted small">{reply.foot}</p>}
          {canConfirm && (
            <div className="inline wrap">
              <button className="btn btn-primary btn-sm" onClick={() => confirm.mutate()} disabled={confirm.isPending}>
                <Icon name="check" size={15} />{confirm.isPending ? 'Doing it…' : reply.action?.outward ? 'Yes, publish it' : 'Yes, do it'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setReply(null)}>No</button>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
